/**
 * =============================================================================
 * IDS CRM Web - Ops Planning Board
 * =============================================================================
 *
 * Gareth, 16 October 2026 (docs/OPS_PLANNING_BOARD_SPEC.md, release 1): the
 * weekly planning meeting's Kanban board.
 *
 *  - Every open product line of an opportunity is a card. New ones arrive in
 *    Holding and are moved by hand. Moving a card never changes its stage.
 *  - The board scrolls on its own, sideways and up and down. The details and
 *    notes panel stays docked at the bottom; it can be collapsed and resized.
 *  - The planning meeting (operations coordinators, team leaders, managers,
 *    admins and finance) drags cards, sets their planning details and adds
 *    notes. Everyone else sees the board read only.
 *  - Ops managers (not finance) add, rename, recolour, reorder and delete
 *    columns. Holding is the only permanent column; a column is deleted only
 *    when it is empty.
 *  - Nothing on the board carries a price.
 *  - Orders (release 2) are cards too, with an Order badge and whether the
 *    order has been accepted. The team accepts an order from its card.
 *
 * The server decides everything (api routes/opsBoard.js); this page only
 * shows the board and sends what people do.
 * =============================================================================
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../components/Toast';
import { getAssetTypeLabel } from '../utils/assetTypes';
import { formatDateKey } from '../utils/dates';
import { formatDateTime } from '../utils/format';
import { canAcceptOrders, canOpenDealFromBoard } from '../utils/permissions';
import './Assets.css';
import './OpsBoard.css';

export const BOARD_READ_FAILED = 'The planning board could not be read. Check your signal and reload the page.';
export const NOTE_MAX = 2000;

export const COLOURS = [
  ['grey', 'Grey'], ['red', 'Red'], ['amber', 'Amber'], ['green', 'Green'], ['teal', 'Teal'],
  ['blue', 'Blue'], ['purple', 'Purple'], ['pink', 'Pink'], ['brown', 'Brown']
];
export const PRIORITIES = [['urgent', 'Urgent'], ['high', 'High'], ['normal', 'Normal'], ['low', 'Low']];
export const WORK_STATUSES = [
  ['not_started', 'Not started'], ['in_progress', 'In progress'], ['blocked', 'Blocked'], ['done', 'Done']
];
export const KINDS = [
  ['', 'Everything'], ['opportunity', 'Opportunities'], ['order', 'Orders'], ['service', 'Service work']
];
// Service work on the board (Gareth 2026-10-21): what kind of work a card is.
export const SERVICE_KIND_LABELS = {
  ticket: ['ticket', 'Ticket'],
  scheduled_task: ['engineer', 'Engineer work'],
  maintenance_task: ['maintenance', 'Maintenance']
};
export const SERVICE_PATHS = {
  ticket: (id) => `/tickets/${id}`,
  scheduled_task: (id) => `/tasks/${id}`,
  maintenance_task: (id) => `/maintenance/${id}`
};
const isService = (card) => card.kind === 'service';
/** Where a card's own routes are: sales cards by line, service cards by card. */
export const cardPath = (card, cardId) => (card && isService(card) ? `/cards/service/${cardId}` : `/cards/${cardId}`);
const humanise = (value) => String(value || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
export const HOLDING_SORTS = [
  ['newest', 'Newest first'],
  ['furthest', 'Furthest along first'],
  ['earliest', 'Least far along first'],
  ['board', 'Board order']
];

const PANEL_MIN = 160;
const PANEL_DEFAULT = 320;
const PANEL_KEY = 'opsBoard.panelHeight';

const labelOf = (pairs, value) => (pairs.find(([v]) => v === value) || [value, value])[1];

// The panel never takes more than 40% of the window, so on a laptop or a
// short window the board keeps most of the screen.
function panelCap() {
  return Math.max(PANEL_MIN, Math.round(window.innerHeight * 0.4));
}

function readPanelHeight() {
  let saved = 0;
  try { saved = Number(localStorage.getItem(PANEL_KEY)); } catch (err) { /* not kept */ }
  return Math.min(saved >= PANEL_MIN ? saved : PANEL_DEFAULT, panelCap());
}

function savePanelHeight(height) {
  try { localStorage.setItem(PANEL_KEY, String(Math.round(height))); } catch (err) { /* not kept */ }
}

async function call(authFetch, path, { method = 'GET', body } = {}) {
  let response;
  try {
    response = await authFetch(`/api/v1/ops-board${path}`, {
      method,
      body: body === undefined ? undefined : JSON.stringify(body)
    });
  } catch (err) {
    throw new Error('That could not be sent. Check your signal and try again.');
  }
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.message || 'That could not be saved. Try again.');
  return data;
}

/** Cards of one column, in the order the column shows them. */
export function orderCards(cards, { isHolding, holdingSort, holdingStage }) {
  let list = cards;
  if (isHolding && holdingStage) list = list.filter(card => card.stage === holdingStage);
  // A service card's "created" is when it was put on the board.
  const createdOf = (card) => String(card.created_at || card.added_at || '');
  const byPosition = (a, b) => (a.position ?? Infinity) - (b.position ?? Infinity)
    || createdOf(b).localeCompare(createdOf(a));
  if (!isHolding || holdingSort === 'board') return [...list].sort(byPosition);
  const newest = (a, b) => createdOf(b).localeCompare(createdOf(a));
  if (holdingSort === 'newest') return [...list].sort(newest);
  const stage = card => (card.stage_order == null ? -1 : card.stage_order);
  const direction = holdingSort === 'furthest' ? -1 : 1;
  return [...list].sort((a, b) => direction * (stage(a) - stage(b)) || newest(a, b));
}

export function matchesFilters(card, filters, userId) {
  if (filters.kind === 'order' && (isService(card) || !card.is_order)) return false;
  if (filters.kind === 'opportunity' && (isService(card) || card.is_order)) return false;
  if (filters.kind === 'service' && !isService(card)) return false;
  if (filters.priority && card.priority !== filters.priority) return false;
  if (filters.assigned === 'me' && card.assigned_to_id !== userId) return false;
  if (filters.assigned === 'none' && card.assigned_to_id) return false;
  if (filters.search) {
    const needle = filters.search.toLowerCase();
    const hay = (isService(card)
      ? [card.ref, card.title, card.customer_name, card.asset_ref, card.engineer_names, card.reason_note]
      : [card.opp_ref, card.group_ref, card.title, card.description, card.group_title, card.customer_name,
        card.site_name, card.distributor_name, getAssetTypeLabel(card.product)])
      .filter(Boolean).join(' ').toLowerCase();
    if (!hay.includes(needle)) return false;
  }
  return true;
}

function ServiceCardBody({ card, reasons }) {
  const [badgeClass, badgeText] = SERVICE_KIND_LABELS[card.record_type] || ['', 'Service'];
  return (
    <>
      <span className="ops-card-top">
        <span className="ops-card-ref">{card.ref}</span>
        <span className={`ops-service-badge ${badgeClass}`}>{badgeText}</span>
        <span className="ops-card-title">{card.title}</span>
      </span>
      <span className="ops-card-line">
        {[card.customer_name, card.asset_ref].filter(Boolean).join(' · ') || 'No customer'}
      </span>
      <span className="ops-card-meta">
        <span className="ops-service-reason">{(reasons && reasons[card.reason]) || humanise(card.reason)}</span>
        <span className="ops-card-stage">{humanise(card.record_status)}</span>
        <span className={`ops-priority ops-priority-${card.priority}`}>{labelOf(PRIORITIES, card.priority)}</span>
      </span>
    </>
  );
}

function BoardCard({ card, reasons, selected, canPlan, onSelect, onDragStart, onDragOverCard, dropBefore }) {
  return (
    <>
      {dropBefore && <div className="ops-drop-marker" />}
      <button
        type="button"
        className={`ops-card${isService(card) ? ' ops-card-service' : ''}${selected ? ' selected' : ''}`}
        draggable={canPlan}
        onDragStart={(event) => onDragStart(event, card)}
        onDragOver={(event) => onDragOverCard(event, card)}
        onClick={() => onSelect(card.card_id)}
        aria-pressed={selected}
      >
        {isService(card) ? <ServiceCardBody card={card} reasons={reasons} /> : (
        <>
        <span className="ops-card-top">
          <span className="ops-card-ref">{card.opp_ref || card.group_ref || 'New'}</span>
          {card.is_order && <span className="order-badge">Order</span>}
          <span className="ops-card-title">{card.title}</span>
        </span>
        {card.description && <span className="ops-card-what">{card.description}</span>}
        <span className="ops-card-line">
          {card.site_name || card.customer_name || 'No customer'}
        </span>
        <span className="ops-card-meta">
          <span className="ops-card-stage">{card.stage}</span>
          {card.is_order && !card.order_accepted_at && <span className="ops-not-accepted">Not accepted</span>}
          <span className={`ops-priority ops-priority-${card.priority}`}>{labelOf(PRIORITIES, card.priority)}</span>
        </span>
        </>
        )}
        {(card.assigned_to_name || card.planned_date || card.work_status !== 'not_started') && (
          <span className="ops-card-meta ops-card-plan">
            {card.assigned_to_name && <span>{card.assigned_to_name}</span>}
            {card.planned_date && <span>{formatDateKey(card.planned_date)}</span>}
            {card.work_status !== 'not_started' && (
              <span className={`ops-status ops-status-${card.work_status}`}>{labelOf(WORK_STATUSES, card.work_status)}</span>
            )}
          </span>
        )}
      </button>
    </>
  );
}

function HistoryList({ detail }) {
  const items = useMemo(() => {
    if (!detail) return [];
    return [
      ...detail.notes.map(n => ({
        key: `n${n.entry_id}`, at: n.occurred_at, who: n.user_name,
        title: n.is_planning_note ? 'Planning meeting note' : (n.subject || n.entry_type.replace(/_/g, ' ')),
        body: n.body
      })),
      ...detail.moves.map(m => ({
        key: `m${m.move_id}`, at: m.moved_at, who: m.moved_by_name,
        // Service cards: put on the board has no "from"; taken off has no "to" column.
        title: isService(detail.card) && !m.from_column_name
          ? `Put on the board in ${m.to_column_name}`
          : isService(detail.card) && m.to_column_name === 'Taken off the board'
            ? `Taken off the board (was in ${m.from_column_name})`
            : `Moved from ${m.from_column_name || 'Holding'} to ${m.to_column_name}`
      })),
      ...detail.stage_changes.map(s => ({
        key: `s${s.audit_id}`, at: s.changed_at, who: s.changed_by_name,
        title: `Stage: ${s.old_value || '-'} → ${s.new_value || '-'}`
      }))
    ].sort((a, b) => String(b.at).localeCompare(String(a.at)));
  }, [detail]);

  if (items.length === 0) return <p className="ops-muted">Nothing recorded yet.</p>;
  return (
    <ul className="ops-history">
      {items.map(item => (
        <li key={item.key}>
          <div className="ops-history-head">
            <strong>{item.title}</strong>
            <span>{formatDateTime(item.at)}{item.who ? ` · ${item.who}` : ''}</span>
          </div>
          {item.body && <p>{item.body}</p>}
        </li>
      ))}
    </ul>
  );
}

function CardPanel({ cardId, board, people, role, onMove, onSaved, onReload, onClose, showToast }) {
  const { authFetch } = useAuth();
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [removing, setRemoving] = useState(false);
  const boardCard = board.cards.find(c => c.card_id === cardId);
  const path = cardPath(boardCard, cardId);

  const load = useCallback(async () => {
    setError('');
    try {
      setDetail(await call(authFetch, path));
    } catch (err) {
      setDetail(null);
      setError(err.message);
    }
  }, [authFetch, path]);

  useEffect(() => { setDetail(null); setNote(''); load(); }, [load]);

  const card = boardCard || detail?.card;
  if (error) return <div className="ops-panel-body"><p className="ops-error">{error}</p></div>;
  if (!card) return <div className="ops-panel-body"><p className="ops-muted">Loading…</p></div>;

  const canPlan = board.can_plan;
  const service = isService(card);
  const save = async (changes) => {
    try {
      await call(authFetch, path, { method: 'PATCH', body: changes });
      onSaved(cardId, changes);
    } catch (err) {
      showToast(err.message, 'error');
    }
  };
  // Service work comes off the board by hand (team leaders and up) or by
  // itself when the work is finished. Its history is kept.
  const takeOff = async () => {
    if (!window.confirm(`Take ${card.ref} off the planning board?`)) return;
    setRemoving(true);
    try {
      await call(authFetch, `/service-cards/${cardId}/remove`, { method: 'POST', body: {} });
      showToast(`${card.ref} taken off the board.`, 'success');
      onClose();
      await onReload();
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setRemoving(false);
    }
  };
  const addNote = async () => {
    const text = note.trim();
    if (!text) return;
    setSaving(true);
    try {
      await call(authFetch, `${path}/notes`, { method: 'POST', body: { body: text } });
      setNote('');
      showToast(service ? `Note added to ${card.ref}.` : 'Note added to the timeline.', 'success');
      load();
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  // Orders: a team member accepts the whole order (spec 3.3). No checklist.
  const acceptOrder = async () => {
    setAccepting(true);
    try {
      const response = await authFetch(`/api/v1/opportunity-groups/${card.group_id}/accept`, {
        method: 'POST', body: JSON.stringify({})
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || 'The order could not be accepted. Try again.');
      showToast(`${card.group_ref} accepted.`, 'success');
      await onReload();
      load();
    } catch (err) {
      showToast(err.message === 'Failed to fetch' ? 'That could not be sent. Check your signal and try again.' : err.message, 'error');
    } finally {
      setAccepting(false);
    }
  };

  const dealLink = canOpenDealFromBoard(role) && card.group_id ? `/pipeline/groups/${card.group_id}` : null;
  const acceptance = card.is_order
    ? (card.order_accepted_at
      ? `Accepted ${formatDateTime(card.order_accepted_at)}${card.order_accepted_by_name ? ` by ${card.order_accepted_by_name}` : ''}`
      : 'Not accepted yet')
    : null;
  const reasonText = service
    ? `${(board.service_reasons && board.service_reasons[card.reason]) || humanise(card.reason)}${card.reason_note ? `: ${card.reason_note}` : ''}`
    : null;
  const recordPath = service && SERVICE_PATHS[card.record_type] ? SERVICE_PATHS[card.record_type](card.record_id) : null;
  const facts = service ? [
    ['Customer', card.customer_name],
    ['Robot', card.asset_ref],
    ['Kind of work', (SERVICE_KIND_LABELS[card.record_type] || [])[1]],
    ['Its status', humanise(card.record_status)],
    ['Engineer', card.engineer_names || 'Nobody yet'],
    ['Complete by', card.due_date ? formatDateKey(card.due_date) : null],
    ['Why it is here', reasonText],
    ['Put here', `${formatDateTime(card.added_at)}${card.added_by_name ? ` by ${card.added_by_name}` : ''}`]
  ].filter(([, value]) => value) : [
    ['Customer', card.customer_name],
    ['Site', card.site_name],
    ['Product', getAssetTypeLabel(card.product)],
    ['What is ordered', card.description],
    ['Pipeline stage', card.stage],
    [card.is_order ? 'Order' : 'Opportunity', dealLink
      ? <Link to={dealLink}>{card.group_ref || 'Open'}{card.group_title ? ` · ${card.group_title}` : ''}</Link>
      : [card.group_ref, card.group_title].filter(Boolean).join(' · ')],
    ['Order accepted', acceptance],
    ['Distributor', card.route === 'distributor' ? card.distributor_name : null],
    ['Owner', card.owner_name],
    ['Added', formatDateKey(String(card.created_at).slice(0, 10))]
  ].filter(([, value]) => value);

  return (
    <div className="ops-panel-body">
      <section className="ops-panel-details">
        <div className="ops-panel-title">
          <span className="ops-card-ref">{service ? card.ref : (card.opp_ref || card.group_ref)}</span>
          <h2>{card.title}</h2>
          {recordPath && (
            <Link to={recordPath} className="btn-primary btn-small">Open {card.record_type === 'ticket' ? 'ticket' : 'job'}</Link>
          )}
          <button type="button" className="ops-link-btn" onClick={onClose}>Close</button>
        </div>
        {service && board.can_add_service && (
          <button type="button" className="btn-secondary-small ops-take-off-btn" disabled={removing} onClick={takeOff}>
            {removing ? 'Taking off…' : 'Take off the board'}
          </button>
        )}
        <dl className="ops-facts">
          {facts.map(([label, value]) => (
            <React.Fragment key={label}><dt>{label}</dt><dd>{value}</dd></React.Fragment>
          ))}
        </dl>
        {card.is_order && !card.order_accepted_at && canAcceptOrders(role) && card.group_id && (
          <button type="button" className="btn-primary ops-accept-btn" disabled={accepting} onClick={acceptOrder}>
            {accepting ? 'Accepting…' : 'Accept order'}
          </button>
        )}
        <div className="ops-plan-fields">
          <label>Column
            <select value={card.column_id} disabled={!canPlan}
              onChange={(e) => onMove(cardId, e.target.value, null)}>
              {board.columns.map(col => <option key={col.column_id} value={col.column_id}>{col.name}</option>)}
            </select>
          </label>
          <label>Assigned to
            <select value={card.assigned_to_id || ''} disabled={!canPlan}
              onChange={(e) => save({ assigned_to_id: e.target.value || null })}>
              <option value="">Nobody yet</option>
              {card.assigned_to_id && !people.some(p => p.user_id === card.assigned_to_id) && (
                <option value={card.assigned_to_id}>{card.assigned_to_name}</option>
              )}
              {people.map(p => <option key={p.user_id} value={p.user_id}>{p.full_name}</option>)}
            </select>
          </label>
          <label>Priority
            <select value={card.priority} disabled={!canPlan} onChange={(e) => save({ priority: e.target.value })}>
              {PRIORITIES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </label>
          <label>Status
            <select value={card.work_status} disabled={!canPlan} onChange={(e) => save({ work_status: e.target.value })}>
              {WORK_STATUSES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </label>
          <label>Planned date
            <input type="date" value={card.planned_date ? String(card.planned_date).slice(0, 10) : ''} disabled={!canPlan}
              onChange={(e) => save({ planned_date: e.target.value || null })} />
          </label>
        </div>
      </section>
      <section className="ops-panel-notes">
        {canPlan && (
          <div className="ops-note-box">
            <textarea
              value={note}
              maxLength={NOTE_MAX}
              placeholder="Add a note…"
              aria-label="Add a note"
              onChange={(e) => setNote(e.target.value)}
            />
            <div className="ops-note-actions">
              <span className="ops-muted">{note.length} / {NOTE_MAX}</span>
              <button type="button" className="btn-primary" disabled={saving || !note.trim()} onClick={addNote}>
                {saving ? 'Saving…' : 'Add Note'}
              </button>
            </div>
          </div>
        )}
        <h3>History</h3>
        {detail ? <HistoryList detail={detail} /> : <p className="ops-muted">Loading…</p>}
      </section>
    </div>
  );
}

function ColumnManager({ columns, cards, onChanged, onClose, showToast }) {
  const { authFetch } = useAuth();
  const [names, setNames] = useState(() => Object.fromEntries(columns.map(c => [c.column_id, c.name])));
  const [newName, setNewName] = useState('');
  const [newColour, setNewColour] = useState('grey');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setNames(prev => Object.fromEntries(columns.map(c => [c.column_id, prev[c.column_id] ?? c.name])));
  }, [columns]);

  const run = async (fn, done) => {
    setBusy(true);
    try {
      await fn();
      if (done) showToast(done, 'success');
      await onChanged();
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setBusy(false);
    }
  };
  const countIn = id => cards.filter(card => card.column_id === id).length;
  const move = (index, step) => {
    const ids = columns.map(c => c.column_id);
    const [moved] = ids.splice(index, 1);
    ids.splice(index + step, 0, moved);
    run(() => call(authFetch, '/columns/order', { method: 'PUT', body: { column_ids: ids } }));
  };

  return (
    <div className="ops-modal-overlay" role="dialog" aria-modal="true" aria-label="Manage columns" onClick={onClose}>
      <div className="ops-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ops-modal-head">
          <h2>Manage columns</h2>
          <button type="button" className="ops-link-btn" onClick={onClose}>Done</button>
        </div>
        <p className="ops-muted">
          Holding is permanent: new cards arrive there. Other columns can be renamed, recoloured,
          moved, or deleted once they are empty.
        </p>
        <ul className="ops-column-list">
          {columns.map((col, index) => {
            const count = countIn(col.column_id);
            const renamed = (names[col.column_id] || '').trim() !== col.name;
            return (
              <li key={col.column_id}>
                <span className={`ops-swatch ops-colour-${col.colour}`} />
                <input
                  value={names[col.column_id] ?? ''}
                  maxLength={60}
                  disabled={col.is_holding || busy}
                  aria-label={`Name of ${col.name}`}
                  onChange={(e) => setNames(prev => ({ ...prev, [col.column_id]: e.target.value }))}
                />
                {renamed && !col.is_holding && (
                  <button type="button" className="btn-secondary-small" disabled={busy}
                    onClick={() => run(() => call(authFetch, `/columns/${col.column_id}`, {
                      method: 'PATCH', body: { name: names[col.column_id] }
                    }), 'Column renamed.')}>Save name</button>
                )}
                <select value={col.colour} disabled={busy} aria-label={`Colour of ${col.name}`}
                  onChange={(e) => run(() => call(authFetch, `/columns/${col.column_id}`, {
                    method: 'PATCH', body: { colour: e.target.value }
                  }))}>
                  {COLOURS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select>
                <span className="ops-muted ops-column-count">{count} card{count === 1 ? '' : 's'}</span>
                {!col.is_holding && (
                  <>
                    <button type="button" className="btn-secondary-small" aria-label={`Move ${col.name} left`}
                      disabled={busy || index <= 1} onClick={() => move(index, -1)}>←</button>
                    <button type="button" className="btn-secondary-small" aria-label={`Move ${col.name} right`}
                      disabled={busy || index >= columns.length - 1} onClick={() => move(index, 1)}>→</button>
                    <button type="button" className="btn-small-danger"
                      disabled={busy || count > 0}
                      title={count > 0 ? 'Move its cards to another column first' : 'Delete this column'}
                      onClick={() => {
                        if (!window.confirm(`Delete the "${col.name}" column?`)) return;
                        run(() => call(authFetch, `/columns/${col.column_id}`, { method: 'DELETE' }), 'Column deleted.');
                      }}>Delete</button>
                  </>
                )}
                {col.is_holding && <span className="ops-muted">Permanent</span>}
              </li>
            );
          })}
        </ul>
        <form className="ops-column-add" onSubmit={(e) => {
          e.preventDefault();
          if (!newName.trim()) return;
          run(async () => {
            await call(authFetch, '/columns', { method: 'POST', body: { name: newName, colour: newColour } });
            setNewName('');
          }, 'Column added.');
        }}>
          <input value={newName} maxLength={60} placeholder="New column name" aria-label="New column name"
            onChange={(e) => setNewName(e.target.value)} />
          <select value={newColour} aria-label="New column colour" onChange={(e) => setNewColour(e.target.value)}>
            {COLOURS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
          <button type="submit" className="btn-primary" disabled={busy || !newName.trim()}>Add column</button>
        </form>
      </div>
    </div>
  );
}

export default function OpsBoard() {
  const { authFetch, user } = useAuth();
  const { toastElement, showToast } = useToast();
  const [board, setBoard] = useState(null);
  const [error, setError] = useState('');
  const [people, setPeople] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [filters, setFilters] = useState({ search: '', kind: '', column: '', priority: '', assigned: '' });
  const [holdingSort, setHoldingSort] = useState('newest');
  const [holdingStage, setHoldingStage] = useState('');
  const [managing, setManaging] = useState(false);
  const [panelOpen, setPanelOpen] = useState(true);
  const [panelHeight, setPanelHeight] = useState(readPanelHeight);
  const [drop, setDrop] = useState(null); // { columnId, beforeId }
  const dragId = useRef(null);

  const load = useCallback(async () => {
    try {
      const data = await call(authFetch, '');
      setBoard(data);
      setError('');
    } catch (err) {
      setError(err.message === 'That could not be sent. Check your signal and try again.' ? BOARD_READ_FAILED : err.message);
    }
  }, [authFetch]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!board?.can_plan) return;
    call(authFetch, '/people').then(data => setPeople(data.data || [])).catch(() => setPeople([]));
  }, [authFetch, board?.can_plan]);

  // Drop the selection when its card has left the board.
  useEffect(() => {
    if (board && selectedId && !board.cards.some(c => c.card_id === selectedId)) setSelectedId(null);
  }, [board, selectedId]);

  const moveCard = useCallback(async (cardId, columnId, beforeId) => {
    if (beforeId === cardId) return;
    const moving = board?.cards.find(card => card.card_id === cardId);
    try {
      const result = await call(authFetch, `${cardPath(moving, cardId)}/move`, {
        method: 'POST', body: { column_id: columnId, before_id: beforeId || null }
      });
      const rank = new Map(result.order.map((id, index) => [id, index + 1]));
      setBoard(prev => prev && ({
        ...prev,
        cards: prev.cards.map(card => {
          if (card.card_id === cardId) return { ...card, column_id: columnId, position: rank.get(cardId) };
          return rank.has(card.card_id) ? { ...card, position: rank.get(card.card_id) } : card;
        })
      }));
    } catch (err) {
      showToast(err.message, 'error');
      load();
    }
  }, [authFetch, board, load, showToast]);

  const onSaved = useCallback((cardId, changes) => {
    setBoard(prev => prev && ({
      ...prev,
      cards: prev.cards.map(card => {
        if (card.card_id !== cardId) return card;
        const next = { ...card, ...changes };
        if ('assigned_to_id' in changes) {
          next.assigned_to_name = people.find(p => p.user_id === changes.assigned_to_id)?.full_name || null;
        }
        return next;
      })
    }));
  }, [people]);

  const startResize = (event) => {
    event.preventDefault();
    const startY = event.clientY;
    const startHeight = panelHeight;
    const max = Math.max(PANEL_MIN, Math.round(window.innerHeight * 0.6));
    let latest = startHeight;
    const onMove = (e) => {
      latest = Math.min(max, Math.max(PANEL_MIN, startHeight + (startY - e.clientY)));
      setPanelHeight(latest);
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      savePanelHeight(latest);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const holdingId = board?.columns.find(c => c.is_holding)?.column_id;
  const holdingStages = useMemo(() => {
    if (!board) return [];
    const seen = new Map();
    board.cards.filter(c => c.column_id === holdingId).forEach(c => {
      if (c.stage && !seen.has(c.stage)) seen.set(c.stage, c.stage_order ?? -1);
    });
    return [...seen.entries()].sort((a, b) => a[1] - b[1]).map(([stage]) => stage);
  }, [board, holdingId]);

  if (error && !board) {
    return <div className="assets-page"><p className="ops-error">{error}</p></div>;
  }
  if (!board) return <div className="assets-page"><p className="ops-muted">Loading the board…</p></div>;

  const visibleCards = board.cards.filter(card => matchesFilters(card, filters, user?.userId));
  const columns = filters.column ? board.columns.filter(c => c.column_id === filters.column) : board.columns;
  const canPlan = board.can_plan;

  const onDragStart = (event, card) => {
    if (!canPlan) return;
    dragId.current = card.card_id;
    event.dataTransfer.effectAllowed = 'move';
    try { event.dataTransfer.setData('text/plain', card.card_id); } catch (err) { /* older Safari */ }
  };
  const onDragOverCard = (event, card) => {
    if (!dragId.current) return;
    event.preventDefault();
    event.stopPropagation();
    const box = event.currentTarget.getBoundingClientRect();
    const after = event.clientY > box.top + box.height / 2;
    const list = orderCards(visibleCards.filter(c => c.column_id === card.column_id), {
      isHolding: card.column_id === holdingId, holdingSort, holdingStage
    });
    const index = list.findIndex(c => c.card_id === card.card_id);
    const beforeId = after ? (list[index + 1]?.card_id || null) : card.card_id;
    setDrop({ columnId: card.column_id, beforeId });
  };
  const onDropColumn = (event, columnId) => {
    event.preventDefault();
    const cardId = dragId.current;
    const beforeId = drop?.columnId === columnId ? drop.beforeId : null;
    dragId.current = null;
    setDrop(null);
    if (cardId) moveCard(cardId, columnId, beforeId);
  };

  return (
    <div className="ops-page">
      {toastElement}
      <div className="page-header ops-header">
        <div>
          <h1>Ops Planning</h1>
          <p>
            Every opportunity's and order's products, for the weekly planning meeting, and any service work a team
            leader or manager has put here. New ones arrive in Holding. Moving a card does not change its pipeline
            stage or the ticket or job.
          </p>
        </div>
        {board.can_manage_columns && (
          <button type="button" className="btn-secondary" onClick={() => setManaging(true)}>Manage columns</button>
        )}
      </div>

      <div className="ops-filters">
        <input type="search" placeholder="Search customer, site, product or reference" aria-label="Search the board"
          value={filters.search} onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))} />
        <select aria-label="Show opportunities or orders" value={filters.kind} onChange={(e) => setFilters(f => ({ ...f, kind: e.target.value }))}>
          {KINDS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select>
        <select aria-label="Show column" value={filters.column} onChange={(e) => setFilters(f => ({ ...f, column: e.target.value }))}>
          <option value="">All columns</option>
          {board.columns.map(c => <option key={c.column_id} value={c.column_id}>{c.name}</option>)}
        </select>
        <select aria-label="Show priority" value={filters.priority} onChange={(e) => setFilters(f => ({ ...f, priority: e.target.value }))}>
          <option value="">All priorities</option>
          {PRIORITIES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select>
        <select aria-label="Show assigned" value={filters.assigned} onChange={(e) => setFilters(f => ({ ...f, assigned: e.target.value }))}>
          <option value="">Anyone</option>
          <option value="me">Assigned to me</option>
          <option value="none">Not assigned</option>
        </select>
        {!canPlan && <span className="ops-muted">You can look at the board; the planning meeting moves the cards.</span>}
      </div>

      <div className="ops-board" role="region" aria-label="Planning board">
        {columns.map(col => {
          const isHolding = col.column_id === holdingId;
          const list = orderCards(visibleCards.filter(c => c.column_id === col.column_id), { isHolding, holdingSort, holdingStage });
          const isDropTarget = drop?.columnId === col.column_id;
          return (
            <section
              key={col.column_id}
              className={`ops-column ops-colour-${col.colour}${isDropTarget ? ' drop-target' : ''}`}
              aria-label={col.name}
              onDragOver={(event) => {
                if (!dragId.current) return;
                event.preventDefault();
                if (drop?.columnId !== col.column_id) setDrop({ columnId: col.column_id, beforeId: null });
              }}
              onDragLeave={(event) => {
                if (!event.currentTarget.contains(event.relatedTarget)) setDrop(d => (d?.columnId === col.column_id ? null : d));
              }}
              onDrop={(event) => onDropColumn(event, col.column_id)}
            >
              <header className="ops-column-head">
                <h2>{col.name}</h2>
                <span className="ops-count">{list.length}</span>
              </header>
              {isHolding && (
                <div className="ops-holding-tools">
                  <select aria-label="Sort Holding" value={holdingSort} onChange={(e) => setHoldingSort(e.target.value)}>
                    {HOLDING_SORTS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                  </select>
                  <select aria-label="Holding stage" value={holdingStage} onChange={(e) => setHoldingStage(e.target.value)}>
                    <option value="">All stages</option>
                    {holdingStages.map(stage => <option key={stage} value={stage}>{stage}</option>)}
                  </select>
                </div>
              )}
              <div className="ops-column-cards">
                {list.map(card => (
                  <BoardCard
                    key={card.card_id}
                    card={card}
                    reasons={board.service_reasons}
                    selected={card.card_id === selectedId}
                    canPlan={canPlan}
                    onSelect={(id) => { setSelectedId(id); setPanelOpen(true); }}
                    onDragStart={onDragStart}
                    onDragOverCard={onDragOverCard}
                    dropBefore={isDropTarget && drop.beforeId === card.card_id}
                  />
                ))}
                {isDropTarget && drop.beforeId === null && <div className="ops-drop-marker" />}
                {list.length === 0 && <p className="ops-empty">No cards</p>}
              </div>
            </section>
          );
        })}
      </div>

      <div className={`ops-panel${panelOpen ? '' : ' collapsed'}`} style={panelOpen ? { height: panelHeight } : undefined}>
        {panelOpen && <div className="ops-panel-resize" onMouseDown={startResize} title="Drag to resize" aria-hidden="true" />}
        <div className="ops-panel-bar">
          <strong>{selectedId ? 'Card details and notes' : 'Details and notes'}</strong>
          <button type="button" className="ops-link-btn" onClick={() => setPanelOpen(o => !o)} aria-expanded={panelOpen}>
            {panelOpen ? 'Collapse' : 'Show'}
          </button>
        </div>
        {panelOpen && (selectedId ? (
          <CardPanel
            cardId={selectedId}
            board={board}
            people={people}
            role={user?.role}
            onMove={moveCard}
            onSaved={onSaved}
            onReload={load}
            onClose={() => setSelectedId(null)}
            showToast={showToast}
          />
        ) : (
          <div className="ops-panel-body"><p className="ops-muted">Click a card to see its details and add a note.</p></div>
        ))}
      </div>

      {managing && (
        <ColumnManager columns={board.columns} cards={board.cards} onChanged={load} onClose={() => setManaging(false)} showToast={showToast} />
      )}
    </div>
  );
}
